/** Routing, with the drawer's state in the URL.
 *
 * ADR-0027 chose TanStack Router for one specific reason: **typed search parameters**. The
 * receipt drawer is addressed by URL so a receipt is shareable — `docs/design/32-information-
 * architecture.md` requires it, and in practice it means a support conversation or an auditor
 * exchange is a pasted link rather than a description of where to click.
 *
 * Because the parameters are validated at the route, every screen receives `{ org, method,
 * receipt, breakdown }` already typed. The alternative was hand-written `URLSearchParams` parsing
 * repeated on five screens, which is exactly the kind of thing that drifts.
 */

import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  type AnySchema,
  type SearchMiddleware,
} from "@tanstack/react-router";

import { AccountSettingsScreen } from "./screens/AccountSettings";
import { AdminScreen } from "./screens/Admin";
import { AssistantSettingsScreen } from "./screens/AssistantSettings";
import { BillingScreen } from "./screens/Billing";
import { ActivityScreen } from "./screens/Activity";
import { AuditScreen } from "./screens/Audit";
import { BulkScreen } from "./screens/Bulk";
import { StructureScreen } from "./screens/Structure";
import { FinancialsScreen } from "./screens/Financials";
import { ImportScreen } from "./screens/Import";
import { ImportedScreen } from "./screens/Imported";
import { JoinScreen } from "./screens/Join";
import { ResetPasswordScreen } from "./screens/ResetPassword";
import { LedgerScreen } from "./screens/Ledger";
import { LedgerEntryScreen } from "./screens/LedgerEntry";
import { AssuranceScreen } from "./screens/Assurance";
import { EngagementScreen } from "./screens/Engagement";
import { ApprovalsScreen } from "./screens/Approvals";
import { PeriodCloseScreen } from "./screens/PeriodClose";
import { FactorDetailScreen } from "./screens/FactorDetail";
import { FactorLibraryScreen } from "./screens/FactorLibrary";
import { PeopleScreen } from "./screens/People";
import { PersonScreen } from "./screens/Person";
import { ProfileScreen } from "./screens/Profile";
import { NotificationsScreen } from "./screens/Notifications";
import { PersonalRequestsScreen } from "./screens/PersonalRequests";
import { HomeScreen } from "./screens/Home";
import { ApplicabilityScreen } from "./screens/filings/Applicability";
import { DashboardsScreen } from "./screens/Dashboards";
import { AskScreen } from "./screens/Stage";
import { SupportScreen } from "./screens/Support";
import { InventoryScreen } from "./screens/Inventory";
import { RecordScreen } from "./screens/Record";
import { ReportsScreen } from "./screens/Reports";
import { RiskScreen } from "./screens/Risk";
import { RisksScreen } from "./screens/Risks";
import { TransitionScreen } from "./screens/Transition";
import { HelpScreen } from "./shell/Help";
import { followRouter } from "./shell/navigationTrail";
import { REDIRECTS } from "./shell/routeTable";
import { ScreenFailed } from "./shell/ScreenFailed";
import { TasksScreen } from "./screens/Tasks";
import { ReviewScreen } from "./screens/Review";
import { Scope1Screen } from "./screens/Scope1";
import { SupplierAccountScreen } from "./screens/SupplierAccount";
import { Scope2Screen } from "./screens/Scope2";
import { Scope3Screen } from "./screens/Scope3";
import { SignInScreen } from "./screens/SignIn";
import { searchForScreen, validateSearch } from "./screens/search";
import { SiteDetailScreen } from "./screens/SiteDetail";
import { DataReportsScreen } from "./screens/dataReports/DataReports";
import { DocumentsScreen } from "./screens/Documents";
import { FacilitiesScreen } from "./screens/Facilities";
import { ProcessingScreen } from "./screens/Processing";
import { UploadScreen } from "./screens/Upload";
import { VendorsScreen } from "./screens/Vendors";
import { SourceDetailScreen } from "./screens/SourceDetail";

/** The search-parameter contract lives in `screens/search.ts`, not here.
 *
 * It moved for one reason: the register's filters are now part of it (EXP-21), and a whitelist that
 * drops a parameter is worse than none — the writer and the reader disagree and neither fails.
 * `search.test.ts` round-trips every key, which a test cannot do from this file without
 * constructing the whole route tree to assert a string.
 */

const rootRoute = createRootRoute({
  component: Outlet,
  errorComponent: ({ reset }) => <ScreenFailed reset={reset} />,
});

/** Home (v5, stage F1). It was the filing cockpit; that moved to `/readiness`, under Reports &
 * filings, where a filing is prepared. A bookmark to `/` now opens Home. */
const home = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  validateSearch,
  component: HomeScreen,
});

const readiness = createRoute({
  getParentRoute: () => rootRoute,
  path: "/readiness",
  validateSearch,
  component: ApplicabilityScreen,
});

/** The three places that land as honest shells (`screens/Stage.tsx`). */
const ask = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ask",
  validateSearch,
  component: AskScreen,
});

const dashboards = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboards",
  validateSearch,
  component: DashboardsScreen,
});

const support = createRoute({
  getParentRoute: () => rootRoute,
  path: "/support",
  validateSearch,
  component: SupportScreen,
});

/** An address that has no screen of its own lands on the place that has the content
 * (`routeTable.ts` `REDIRECTS`): the v5 vocabulary, and the screens ADR-0212 moved (`/evidence` to
 * Documents, `/sources` to Processing's Missing tab, `/suppliers` to Vendors, `/organisation` to
 * Facilities). The query string travels, because it carries the reporting window and the Scope 2
 * basis, and a link that lost them would change which figures it opened. A destination that is a
 * tab of a shared path gets its `?tab=`.
 *
 * **The path is generic so a literal stays a literal.** The router's types list every route by its
 * path, and dozens of typed links across the screens still say `to="/evidence"`; a route whose path
 * is only a `string` would drop that address out of the type and fail every one of them. Those four
 * are declared below by their literal; the rest of the table is generated. */
function redirectRoute<P extends string>(from: P) {
  const target = REDIRECTS[from];
  return createRoute({
    getParentRoute: () => rootRoute,
    path: from,
    validateSearch,
    beforeLoad: ({ search }) => {
      if (!target) return;
      // The router's documented redirect mechanism is a thrown control-flow object, not an Error.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: target.to as "/",
        search: (target.tab ? { ...search, tab: search.tab ?? target.tab } : search) as never,
      });
    },
  });
}

const evidence = redirectRoute("/evidence");
const sources = redirectRoute("/sources");
const suppliers = redirectRoute("/suppliers");
const organisation = redirectRoute("/organisation");
const settingsAlias = redirectRoute("/settings");
const administrationAlias = redirectRoute("/administration");
const MOVED = [
  "/evidence",
  "/sources",
  "/suppliers",
  "/organisation",
  "/settings",
  "/administration",
];

const redirects = Object.keys(REDIRECTS)
  .filter((from) => !MOVED.includes(from))
  .map((from) => redirectRoute(from));

/** The six places the menu has that the product did not (ADR-0212). Each opens a stub the owning
 * package replaces (`screens/PlaceStub.tsx`), so building a place touches neither this file nor the
 * route table. */
const upload = createRoute({
  getParentRoute: () => rootRoute,
  path: "/upload",
  validateSearch,
  component: UploadScreen,
});

const processing = createRoute({
  getParentRoute: () => rootRoute,
  path: "/processing",
  validateSearch,
  component: ProcessingScreen,
});

const documents = createRoute({
  getParentRoute: () => rootRoute,
  path: "/documents",
  validateSearch,
  component: DocumentsScreen,
});

const vendors = createRoute({
  getParentRoute: () => rootRoute,
  path: "/vendors",
  validateSearch,
  component: VendorsScreen,
});

const facilities = createRoute({
  getParentRoute: () => rootRoute,
  path: "/facilities",
  validateSearch,
  component: FacilitiesScreen,
});

/** The person's own settings (KD, 2026-11-01): in the avatar menu, not the rail. `/settings` redirects
 * here. */
const mySettings = createRoute({
  getParentRoute: () => rootRoute,
  path: "/my-settings",
  validateSearch,
  component: AccountSettingsScreen,
});

/** The company Admin's place (KD, 2026-11-01): people and roles, approval routes, security,
 * integrations, company, data, billing and usage, and the admin log. `/administration` redirects
 * here. */
const admin = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  validateSearch,
  component: AdminScreen,
});

/** The Reports library (decision 6b), not the filings: Filings keeps `/reports`. */
const dataReports = createRoute({
  getParentRoute: () => rootRoute,
  path: "/data-reports",
  validateSearch,
  component: DataReportsScreen,
});

/** The inventory register (ADR-0035 step two).
 *
 * `/emissions` keeps its path — it is the one people have bookmarked — and gains the register.
 * `EmissionsScreen` is retired; `/scope-1` and `/scope-2` stay as routes that redirect nothing
 * yet, because a shared link must not 404 while the rail no longer offers them.
 */
const emissions = createRoute({
  getParentRoute: () => rootRoute,
  path: "/emissions",
  validateSearch,
  component: InventoryScreen,
});

const scope1 = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scope-1",
  validateSearch,
  component: Scope1Screen,
});

const scope2 = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scope-2",
  validateSearch,
  component: Scope2Screen,
});

/** Scope 3 (gate 19).
 *
 * Beside Scope 1 and 2 rather than inside the emissions register, for the same reason those two
 * are: the register answers "what did we record", and a scope screen answers "what does this
 * scope consist of" - which for Scope 3 is fifteen categories, most of which a given company has
 * not assessed. An unassessed category says so; it never renders `0.0`, because a zero is a claim
 * and "not assessed" is the absence of one, and only one of those is true.
 */
const scope3 = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scope-3",
  validateSearch,
  component: Scope3Screen,
});

/** Who is in this workspace (S11).
 *
 * Under "Workspace" beside the overview: administering people is not part of measuring or
 * disclosing, and putting it in either would suggest it is.
 */
const people = createRoute({
  getParentRoute: () => rootRoute,
  path: "/people",
  validateSearch,
  component: PeopleScreen,
});

/** One person's page (Batch 5). Reached from a row on People, or from an identifier elsewhere that
 * an Admin may follow. `$userId` is the user's id and **authorizes nothing**: the page reads the
 * Admin-only roster and finds the id in it, so a non-Admin, or an id that is not a member of this
 * workspace, is told the same thing. */
const person = createRoute({
  getParentRoute: () => rootRoute,
  path: "/people/$userId",
  validateSearch,
  component: PersonScreen,
});

/** How this workspace is set up (S11, EXP-22).
 *
 * Beside People under "Workspace", and **not** inside it: people is one of its eight sections,
 * and nesting the whole administration surface under the one part of it that works would make
 * the other seven look like they belong to people administration.
 */
/** One site, in full (Batch 5). Under `/organisation/` because a site is part of the shape of the
 * company, and reached from the site's name on the Organisation screen. Readable by every role that
 * reads the site list: it is a view over reads that already exist, and it carries no write. */
const siteDetail = createRoute({
  getParentRoute: () => rootRoute,
  path: "/organisation/sites/$facilityId",
  validateSearch,
  component: SiteDetailScreen,
});

/** One supplier's account page (EXP-14, ADR-0063/0109).
 *
 * Beside the exchange rather than under it in the rail: it is reached *from* a supplier on the
 * exchange or the responsiveness list, and `$supplierId` is the supplier's **workspace** id - the
 * only identity this workspace holds for another company. Nothing about it is authorized by the id:
 * the page filters rows this session can already read.
 */
const supplierAccount = createRoute({
  getParentRoute: () => rootRoute,
  path: "/suppliers/$supplierId",
  validateSearch,
  component: SupplierAccountScreen,
});

const risks = createRoute({
  getParentRoute: () => rootRoute,
  path: "/risks",
  validateSearch,
  component: RisksScreen,
});

/** One risk's page (Batch 5): its facts from the register's own read, its actions, and the way on to
 * its notes, flags and approvals. `$riskId` is the risk's id, which is also its record id. */
const risk = createRoute({
  getParentRoute: () => rootRoute,
  path: "/risks/$riskId",
  validateSearch,
  component: RiskScreen,
});

/** The transition plan (EXP-20).
 *
 * Under "Act & disclose" beside the risk register: a plan and a risk are both statements about what
 * the company intends, which is a different kind of thing from a measurement.
 */
const transition = createRoute({
  getParentRoute: () => rootRoute,
  path: "/transition",
  validateSearch,
  component: TransitionScreen,
});

/** The person's own pages, reached from the user menu rather than the rail (card C-A, gate 30/32).
 *
 * Not in `NAV`: they are about the person and not about the work, and a rail destination for them
 * would sit beside "Overview" as though it were a place the work happens. Their trail back is the
 * breadcrumb. Like every screen they are not gated here — the API decides what may be read, and
 * these routes act on the caller whatever the role.
 */
const profile = createRoute({
  getParentRoute: () => rootRoute,
  path: "/profile",
  validateSearch,
  component: ProfileScreen,
});

/** The AI assistant's settings (ADR-0192): the Admin's tab of Settings. A non-Admin who types the
 * address is told whose decision it is and nothing is read. */
const assistantSettings = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/ai",
  validateSearch,
  component: AssistantSettingsScreen,
});

/** Billing & usage: the Admin's tab of Settings. It shows what the product records and holds for the
 * workspace and nothing about money. A non-Admin is told whose screen it is and nothing is read. */
const billingSettings = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/billing",
  validateSearch,
  component: BillingScreen,
});

/** Where the menu's Help item goes. A placeholder that says it is one — see `shell/Help.tsx`. */
const help = createRoute({
  getParentRoute: () => rootRoute,
  path: "/help",
  validateSearch,
  component: HelpScreen,
});

/** The audit ledger — Phase F's *"ledger UI and auditor role end to end"*.
 *
 * Under "Workspace" beside Administration, and that is the closest fit rather than an obvious one:
 * Administration is a read-only account of what the product does, and this is a read-only account
 * of what it has recorded. It is not under "Act & disclose", because the chain is not a statement
 * the company makes — it is the record that makes the statements checkable.
 *
 * Not gated in the router, like every other screen: the API decides what may be read, and a route
 * guard would be a second place that decides who sees what — the one that can be bypassed by
 * editing JavaScript. The screen renders, and it tells a role that may not read the chain who can.
 */
const ledger = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ledger",
  validateSearch,
  component: LedgerScreen,
});

/** The activity view (B11): the same record as the ledger, filtered, one plain sentence per act.
 *
 * Beside the ledger because both are read-only accounts of what has been recorded, and apart from
 * it because a filtered list cannot be verified - this screen carries no check and says so. Not
 * gated in the router, like every screen: the API decides who reads it and the screen says who. */
const activity = createRoute({
  getParentRoute: () => rootRoute,
  path: "/activity",
  validateSearch,
  component: ActivityScreen,
});

/** One ledger entry (Batch 5): `$seq` is the chain's own sequence number, a dense integer. The
 * page is read-only, like the list it is reached from. */
const ledgerEntry = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ledger/$seq",
  validateSearch,
  component: LedgerEntryScreen,
});

/** The assurance queue (gate 27, ADR-0048).
 *
 * Beside the ledger, because both are read-only accounts of what the product has recorded rather
 * than statements the company makes — and an assurance provider arrives at them together.
 *
 * **It is the queue only.** A scoped auditor reading the *chain* is refused, and that refusal is
 * ADR-0048's item 5 ("do not add that permission before D's table exists"), not an oversight:
 * a ledger entry has no period, and a periodless record counts as visible, so "clamped to the
 * engagement" has no meaning for the chain yet. `BUILD/blocked/S13-auditor.md` has the options.
 */
const assurance = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assurance",
  validateSearch,
  component: AssuranceScreen,
});

/** The Auditor's overview: the first tab of Approvals & audit for an Auditor (U7). Read-only; the
 * screen says so to any other role that arrives by link. */
const engagement = createRoute({
  getParentRoute: () => rootRoute,
  path: "/engagement",
  validateSearch,
  component: EngagementScreen,
});

/** The factor library (gate 20, ADR-0012).
 *
 * Read-only, and deliberately so: factor sets are **immutable and locked per period**, so there is
 * no edit control — and no edition selector either, because the library holds exactly one edition
 * per jurisdiction and a selector over one choice is scaffolding tested against itself.
 *
 * It reads the **database** `factor_set` rows rather than the engine's in-memory sets, because the
 * rows are what a receipt cites; a screen showing a different source would be a second home for the
 * same fact. A test asserts the two agree — and says of itself that this proves little while both
 * derive from `factors/data.py`.
 */
const factors = createRoute({
  getParentRoute: () => rootRoute,
  path: "/factors",
  validateSearch,
  component: FactorLibraryScreen,
});

/** One factor, read in full (Batch 5). Reached from a row of the library above.
 *
 * `$factorId` is **not an id the server issued** - the library gives a factor none. It is the row's
 * own key (set, category, fuel or carrier, region, unit), built and matched by `screens/factorRoute`,
 * and the page finds it in the same `GET /factor-sets` read the library uses. Nothing about it is
 * authorized by the id: it names a row of reference data every session can read.
 */
const factorDetail = createRoute({
  getParentRoute: () => rootRoute,
  path: "/factors/$factorId",
  validateSearch,
  component: FactorDetailScreen,
});

/** The reviewer's queue (gate 23, ADR-0067).
 *
 * Under "Act & disclose" beside Reports: deciding an approval is part of getting a filing out, not
 * part of measuring. **The server answers "what is waiting on me"** — a client-side queue would
 * have to walk every record, and when it stopped early it would print "nothing is waiting on you"
 * over work that is waiting, which is why card C-D refused to build one.
 */
const approvals = createRoute({
  getParentRoute: () => rootRoute,
  path: "/approvals",
  validateSearch,
  component: ApprovalsScreen,
});

/** Period close (v5, stage F3): the checklist, what locking changes, and the way back. The act was
 * on the Issue tab of a filing; this is the same close, seen from the period (ADR-0094). */
const periodClose = createRoute({
  getParentRoute: () => rootRoute,
  path: "/close",
  validateSearch,
  component: PeriodCloseScreen,
});

/** The spreadsheet round trip (S06).
 *
 * Under "Data" beside the evidence queue and the census: it is a way of getting readings **in**,
 * the same as an upload, rather than a view of the figures. What it is not is a second inventory
 * screen — the register is where figures are read.
 */
const bulk = createRoute({
  getParentRoute: () => rootRoute,
  path: "/bulk",
  validateSearch,
  component: BulkScreen,
});

/** Register your structure (ADR-0091): sites, meters and expected sources from one file.
 *
 * Reached from the Organisation screen rather than from the rail: it is a way of setting up what
 * the Organisation screen shows, and an Admin's one-time job rather than a destination a person
 * returns to. Create-only and all-or-nothing, which is why it is not a mode of Bulk edit.
 */
const structure = createRoute({
  getParentRoute: () => rootRoute,
  path: "/organisation/structure",
  validateSearch,
  component: StructureScreen,
});

/** One source, in full (Batch 5). Reached from a stream's name on the Sources screen and from the
 * site page's Sources tab. `$sourceId` is the expected source's own id, the same one
 * `/records/$recordId` accepts for kind `expected_source`; the page reads the census for the
 * reporting window in force and finds the source in it, so nothing is authorised by the id. */
const sourceDetail = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sources/$sourceId",
  validateSearch,
  component: SourceDetailScreen,
});

/** The work queue (S05, EXP-24).
 *
 * Under "Workspace" rather than beside the inventory: it is where a person starts their day, not
 * a view of the figures. A task may reference any record or none at all.
 */
const tasks = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tasks",
  validateSearch,
  component: TasksScreen,
});

/** The record page (ADR-0035, step three).
 *
 * `$recordId` is the **record** id, which for an activity record is the activity record's own id —
 * `Record.id` is the underlying row's id rather than a surrogate (ADR-0037), so one path serves both
 * the inventory endpoint and the work endpoint without a translation nobody would remember to keep.
 *
 * Under `/records/` rather than `/emissions/`: a record is not only an emissions figure. A document,
 * a facility and a meter are records too, and nesting this under the register would make the first
 * of those a move rather than an addition.
 */
const record = createRoute({
  getParentRoute: () => rootRoute,
  path: "/records/$recordId",
  validateSearch,
  component: RecordScreen,
});

const reports = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports",
  validateSearch,
  component: ReportsScreen,
});

/** What the company disclosed before it used EmitIQ, and the reconciliation (CTX-04, ADR-0032).
 *
 * Under "Act & disclose" beside Reports rather than under "Data": an imported figure is not a
 * reading that arrived, it is a **statement the company already made to a regulator** — the same
 * kind of thing a disclosure is, which is why it sits beside one. And the question it answers,
 * *"does this year's report agree with last year's?"*, is asked while filing.
 *
 * `validateSearch` like every other route: the window and the Scope 2 basis belong to the
 * workspace and follow the reader, and a screen that read them from its own state could disagree
 * with the top bar about which year it was showing.
 */
const imported = createRoute({
  getParentRoute: () => rootRoute,
  path: "/prior-disclosures",
  validateSearch,
  component: ImportedScreen,
});

const review = createRoute({
  getParentRoute: () => rootRoute,
  path: "/evidence/$documentId",
  validateSearch,
  component: ReviewScreen,
});

const importReview = createRoute({
  getParentRoute: () => rootRoute,
  path: "/evidence/$documentId/import",
  validateSearch,
  component: ImportScreen,
});

const signIn = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  validateSearch,
  component: SignInScreen,
});

/** Joining a workspace with an invitation token (ADR-0046).
 *
 * Like `/sign-in`, **outside the shell and outside `RequireSession`**: the person arriving here has
 * no session and cannot have one until they have redeemed, so a gate that asked who they are would
 * answer with the sign-in form — the one screen that cannot help them. The token is **not** a
 * search parameter and must not become one: a bearer credential in the address reaches history,
 * server logs and `Referer`, and `validateSearch` would then be the thing that carries it.
 */
const join = createRoute({
  getParentRoute: () => rootRoute,
  path: "/join",
  validateSearch,
  component: JoinScreen,
});

/** Choosing a new password (card C-B). Outside the shell and outside `RequireSession`, for `/join`'s
 * reason: the person arriving here has forgotten the one thing that would give them a session. The
 * token is in the URL *fragment* (`#token=`), which is never sent to the server; the screen reads it
 * once and removes it from the address bar. */
const resetPassword = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  validateSearch,
  component: ResetPasswordScreen,
});

/** Stating a company's financials and running determination (card C-F, ADR-0069). Admin only —
 * the API refuses everyone else, and the screen says who can rather than showing a control. */
const financials = createRoute({
  getParentRoute: () => rootRoute,
  path: "/financials",
  validateSearch,
  component: FinancialsScreen,
});

const audit = createRoute({
  getParentRoute: () => rootRoute,
  path: "/audit",
  validateSearch,
  component: AuditScreen,
});

const personalRequests = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy-requests",
  validateSearch,
  component: PersonalRequestsScreen,
});

/** The notification inbox (ADR-0092, phase 1). Reached from the bell in the bar. */
const notifications = createRoute({
  getParentRoute: () => rootRoute,
  path: "/notifications",
  validateSearch,
  component: NotificationsScreen,
});

/** Anything else goes to Readiness. The filing is the home screen
 * (`docs/design/30-design-principles.md`), so an unknown URL lands on it rather than on an error. */
const catchAll = createRoute({
  getParentRoute: () => rootRoute,
  path: "$",
  validateSearch,
  beforeLoad: ({ search }) => {
    // TanStack Router signals a redirect by throwing a control-flow object the router catches,
    // not an Error. That is the framework's documented mechanism, so the rule is wrong here
    // rather than the code.
    //
    // The window and the basis travel (G-P6-04): `REDIRECTS` promises the query string does, and a
    // catch-all that dropped `from`/`to`/`method` opened Home on different figures than the link named.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: "/", search: search as never });
  },
});

/** Exported so a test can build a router over the **real** tree with its own history.
 *
 * The app's `router` is a singleton over the browser's history, which in jsdom renders nothing and
 * re-renders forever rather than failing — so a test that used it would be testing the harness. The
 * tree is the part worth sharing: a test route tree would stop matching the app's the first time a
 * path changed, and a `Link` to a path the tree does not hold cannot build a URL at all. */
export const routeTree = rootRoute.addChildren([
  home,
  readiness,
  ask,
  dashboards,
  support,
  ...redirects,
  evidence,
  sources,
  suppliers,
  organisation,
  settingsAlias,
  administrationAlias,
  mySettings,
  admin,
  upload,
  processing,
  documents,
  vendors,
  facilities,
  dataReports,
  tasks,
  emissions,
  scope1,
  scope2,
  scope3,
  record,
  bulk,
  people,
  person,
  siteDetail,
  structure,
  profile,
  assistantSettings,
  billingSettings,
  help,
  ledger,
  ledgerEntry,
  activity,
  assurance,
  engagement,
  factors,
  factorDetail,
  approvals,
  periodClose,
  reports,
  imported,
  supplierAccount,
  risks,
  risk,
  transition,
  sourceDetail,
  review,
  importReview,
  signIn,
  join,
  resetPassword,
  financials,
  audit,
  personalRequests,
  notifications,
  catchAll,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  // Scroll restoration is off: nothing scrolls the page, and panels manage their own scroll.
  scrollRestoration: false,
});

// A record page offers the way back to the list it was opened from (EXP-12): the trail keeps the last
// place that was not a record, with its filters and window.
followRouter(router);

/** A screen's own URL state does not follow the reader to another screen (US walkthrough, C7).
 *
 * Every link on the rail spreads the current search onto the next one, so an open receipt, a tab
 * and the last filing travelled with it. A search middleware on each route runs **before** the
 * link's own `search` is applied, so it can drop what belongs to the screen being left without
 * touching what the link sets deliberately. It reads where the reader is now from the router, and
 * does nothing on first load or within one screen: a pasted deep link and an in-page control (open
 * a receipt, change a tab) are unaffected. The rule itself is `searchForScreen`, tested alone.
 *
 * Attached here, after the tree exists, so every route has it and a route added later cannot be
 * forgotten.
 */
for (const route of Object.values(router.routesById)) {
  if (route.id === "__root__") continue;
  const leaveScreenState: SearchMiddleware<AnySchema> = ({ search, next }) =>
    next(searchForScreen(search, router.state.matches.at(-1)?.routeId, route.id));
  route.options.search = {
    ...route.options.search,
    middlewares: [...(route.options.search?.middlewares ?? []), leaveScreenState],
  } as typeof route.options.search;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
